import React, { useMemo } from 'react';
import { WorldSection } from './WorldPageHeader';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { MissingMonsterPhotos } from './MissingMonsterPhotos';
import { MonsterPhotoPackCheck } from './MonsterPhotoPackCheck';
import { resolveImageEntries, useFailedImages } from '../../utils/entityImages';
import { unassignedImages } from '../../data/guide/entityImages';
import type { WorldData } from '../../types/world';

/** Panel del staff: estado de las imágenes, separado de drops, estadísticas, recetas y ubicaciones. */
export function ImageCheck({
  w
}: {
  w: WorldData;
}) {
  const {
    resolved
  } = useMemo(() => resolveImageEntries(w), [w]);
  const failed = useFailedImages();
  const rows = resolved.map(e => {
    const src = e.image?.src;
    const status = !src ? 'Sin imagen' : failed.includes(src) ? 'No carga' : e.image?.needsConfirmation ? 'Integrada · confirmar asociación' : 'Integrada';
    const id = e.kind === 'clase' ? e.classId?.toUpperCase() : e.resolvedMonsterIds.length ? e.resolvedMonsterIds.join(', ') : '—';
    const idNote = e.notInCatalog ? 'no está en 06_monsters: no se muestra' : e.ambiguous ? 'nombre con varios IDs: no se asigna' : '';
    return {
      e,
      status,
      id,
      idNote
    };
  });
  const pendingText = [...rows.filter(r => r.status !== 'Integrada').map(r => `${r.e.label} (${r.e.kind}, ID ${r.id}${r.idNote ? `, ${r.idNote}` : ''}) · ${r.status}${r.e.pending ? ` · ${r.e.pending}` : ''}`), ...unassignedImages.map(u => `Imagen sin asignar · ${u.description} · ${u.question} · ${u.src}`)].join('\n');
  return <WorldSection id="imagenes" title="Imágenes de clases, NPC y monstruos" count={rows.length} data-id="element-1290" data-mp="1ood0u4b1j1ty">
      <p className="mb-3 max-w-3xl text-sm text-gray-400" data-id="element-1291" data-mp="1snvov8y5rt10">
        Imagen proporcionada por el staff: identifica a la entidad, no confirma drops, coordenadas, estadísticas ni recetas. Si una imagen no carga, aparece aquí al intentar mostrarla en este navegador.
      </p>
      <MonsterPhotoPackCheck w={w} data-id="element-1292" data-mp="11l9vzfffre2h" />
      <MissingMonsterPhotos w={w} data-id="element-1293" data-mp="11ml2hn09mzxa" />
      <div className="mb-3" data-id="element-1294" data-mp="11nmmigz9ewr5">
        <CopyTextButton text={pendingText || 'Sin imágenes pendientes.'} label="Copiar imágenes pendientes" data-id="element-1295" data-mp="wn8bcq2d36ow" />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1296" data-mp="1anj8uby6fjj1">
        <table className="w-full min-w-[720px] text-left text-sm" data-id="element-1297" data-mp="16kd1f0zbvmaz">
          <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1298" data-mp="16n8zcpb6eazf">
            <tr data-id="element-1299" data-mp="1kj318odj8031">
              <th className="px-4 py-3 font-semibold" data-id="element-1300" data-mp="1kikuuyx5o5he">Imagen</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1301" data-mp="1fkj9g7c9bu2r">Entidad</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1302" data-mp="1ua7dhgbtp6u6">ID</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1303" data-mp="1u9nya7dk4wju">Estado</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1304" data-mp="1pcfclwt7aadu">Procedencia y asociación</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-id="element-1305" data-mp="23boquqxuiuf6">
            {rows.map(({
            e,
            status,
            id,
            idNote
          }) => <tr key={e.key} data-id="element-1306" data-mp="1ydvl1z0gg2ff">
                <td className="px-4 py-3" data-id="element-1307" data-mp="2hxug7y8ovni3"><EntityImage entry={e} alt={e.label} kind={e.kind === 'monstruo' ? 'monstruo' : 'persona'} data-id="element-1308" data-mp="2hxkpg8ftlcbc" /></td>
                <td className="px-4 py-3 text-white" data-id="element-1309" data-mp="2mvvxcdmk1cqx">{e.label}<span className="block text-xs text-gray-500" data-id="element-1310" data-mp="2mukvijco6f2g">{e.kind}</span></td>
                <td className="px-4 py-3 font-mono text-gray-200" data-id="element-1311" data-mp="28wwdrkdwfo1m">{id}{idNote && <span className="block font-sans text-xs text-amber-100" data-id="element-1312" data-mp="2cxq0iafluwsx">{idNote}</span>}</td>
                <td className="px-4 py-3 text-gray-200" data-id="element-1313" data-mp="1z72xw76q4mrk">{status}{e.pending && <span className="block text-xs text-gray-500" data-id="element-1314" data-mp="1z5jmatw0izw4">{e.pending}</span>}</td>
                <td className="px-4 py-3 text-xs text-gray-400" data-id="element-1315" data-mp="2405xox23gsg7">{e.image ? <>Imagen proporcionada por el staff · {e.image.addedOn}<span className="block" data-id="element-1316" data-mp="1u89lki36b1sz">{e.image.matchedBy}</span></> : '—'}</td>
              </tr>)}
            {unassignedImages.map(u => <tr key={u.src} data-id="element-1317" data-mp="1g9tir28flvg4">
                <td className="px-4 py-3" data-id="element-1318" data-mp="1gausn3dpobw9"><img src={u.src} alt={u.description} loading="lazy" className="h-10 w-10 rounded-lg border border-white/10 object-cover object-[50%_30%]" data-id="element-1319" data-mp="1l7bokjr5jujw" /></td>
                <td className="px-4 py-3 text-white" data-id="element-1320" data-mp="1d9026qswesz9">Sin asignar<span className="block text-xs text-gray-500" data-id="element-1321" data-mp="187arxddi2jc8">{u.description}</span></td>
                <td className="px-4 py-3 font-mono text-gray-200" data-id="element-1322" data-mp="13gu8ncn003hy">—</td>
                <td className="px-4 py-3 text-amber-100" data-id="element-1323" data-mp="13epy4bulg68p">{u.question}</td>
                <td className="px-4 py-3 text-xs text-gray-400" data-id="element-1324" data-mp="zez9oesji27c">Imagen proporcionada por el staff · no se muestra a los jugadores</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </WorldSection>;
}